import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  fetchAllOCRSectionRenderingData,
  fetchCurrentBlacklineDataBySectionOCR,
  fetchPreviousBlacklineDataBySectionOCR,
} from 'store/actions/statement-content-actions-ocr';
import { isElementReportPreviewPage } from 'utils/ocr-annotation-utils';
import StatementContextMenu from './statement-context-menu-ocr';
import HtmlOverlay from './html-overlay';
import ElementDetailsContainerOCR from './element-details-container-ocr';
import classNames from 'classnames';
import BlacklineViewChanges from './blackline-view-changes-ocr';
import NotesDetailsContainerOCR from './notes-details-container-ocr';
import BookmarksDetailsContainerOCR from './bookmarks-details-container-ocr';
import StatementContentSearchComponentOCR from './statement-content-search-component-ocr';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  OCR_CONTAINER_BLOCK,
  SIDE_BY_SIDE_COMPARE_MODE,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import AddNavigationOCRModal from './add-navigation-ocr-modal';
import PageHeaderOcr from './tieout-content-page-wrapper-ocr/page-header-ocr';
import {
  isModeForAddCursor,
  shouldElementBatchSelection,
} from 'utils/modes-utils';
import { areEqual } from 'constants/common/feature-common-utils';

const SectionContentOverlayOCR = ({
  BLOCK,
  shouldShow,
  elementList,
  setShouldShow,
  sectionMetaData,
  isSectionLoaded,
  box,
  ocrWords,
  overlayCanvas,
  contextMenuData,
  isDrawing,
  isContentPanelScrolling,
  isBatchModeSelected,
  scaleWithZoom,
  clearSelection,
  markerDisplayValue,
  calloutDisplayValue,
  showElementStatusAndFlag = true,
  isLeftView,
  blacklineViewMode,
  isSideBySideView,
  numberOfSourceElementSelected,
  numberOfTargetElementSelected,
  notesList,
  contentSearchResult,
  dpi,
  setSectionInView,
  clearSectionInView,
  blacklineCache,
  blacklineGuidDetails,
  setShowAddNavigationModal,
  showAddNavigationModal,
  bookmarkList,
}) => {
  const canvasID = isLeftView
    ? `canvas-left-${sectionMetaData.sectionId}`
    : `canvas-${sectionMetaData.sectionId}`;
  const SEGMENT_LOADING_HEIGHT = 600; // px
  const dispatch = useDispatch();
  const url = useLocation().pathname;
  const isPreviewPage = isElementReportPreviewPage(url);
  const isApiCallMadeToRenderSection = useRef(false);

  const selectMode = useSelector(
    (store) => store.ui.statementPage.modes.selectMode,
  );
  const sideBySideViewMode = useSelector(
    (store) => store.ui.sideBySideView.sideBySideViewMode,
  );
  const leftSectionIdList = useSelector(
    (store) => store.data?.leftStatementContent?.leftSectionIdList,
  );
  const workFlowStatus = useSelector(
    (store) => store.ui?.sideBySideView?.autoTieout?.workFlowStatus,
  );
  const isSnapshotViewLoaded = useSelector(
    (store) => store.ui?.sideBySideView?.trialBalance.isSnapshotViewLoaded,
  );

  //Fetch the OCR section.
  useEffect(() => {
    const _shouldFetchSection =
      !isApiCallMadeToRenderSection.current &&
      shouldShow &&
      !isSectionLoaded &&
      !isContentPanelScrolling;
    if (_shouldFetchSection) {
      dispatch(
        fetchAllOCRSectionRenderingData({
          sectionMetaData,
          isLeftView,
        }),
      );
      isApiCallMadeToRenderSection.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionLoaded, shouldShow, isContentPanelScrolling]);

  useEffect(() => {
    !shouldShow && (isApiCallMadeToRenderSection.current = false);
  }, [shouldShow]);

  //Set sectionsInView using IO.
  useEffect(() => {
    const rootElementId = isLeftView
      ? `${OCR_CONTAINER_BLOCK}__document-id-left-view`
      : `${OCR_CONTAINER_BLOCK}__document-id`;
    const intersectionObserverConfig = {
      root: document.getElementById(rootElementId), //the element that we check for intersection with
      rootMargin: `${SEGMENT_LOADING_HEIGHT}px 0px`,
    };
    const abortController = new AbortController();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!shouldShow) {
            setSectionInView({ sectionId: sectionMetaData.sectionId });
            setShouldShow(true);
          }
        } else {
          if (shouldShow) {
            setShouldShow(false);
            clearSectionInView({ sectionId: sectionMetaData.sectionId });
          }
        }
      });
    }, intersectionObserverConfig);

    const currentOverlayCanvas = overlayCanvas.current;
    if (currentOverlayCanvas) observer.observe(currentOverlayCanvas);
    return () => {
      if (currentOverlayCanvas) {
        observer.unobserve(currentOverlayCanvas);
      }
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionMetaData.sectionId, shouldShow]);

  useEffect(() => {
    if (blacklineViewMode) {
      const rootElementId = isLeftView
        ? `${OCR_CONTAINER_BLOCK}__document-id-left-view`
        : `${OCR_CONTAINER_BLOCK}__document-id`;
      const intersectionObserverConfig = {
        root: document.getElementById(rootElementId), //the element that we check for intersection with
        rootMargin: `${SEGMENT_LOADING_HEIGHT}px 0px`,
      };
      const abortController = new AbortController();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (isLeftView) {
              !!blacklineGuidDetails &&
                dispatch(
                  fetchPreviousBlacklineDataBySectionOCR({
                    sectionId: sectionMetaData.sectionId,
                    blacklineGuidDetails,
                  }),
                );
            } else {
              !!blacklineGuidDetails &&
                dispatch(
                  fetchCurrentBlacklineDataBySectionOCR({
                    sectionId: sectionMetaData.sectionId,
                    blacklineGuidDetails,
                  }),
                );
            }
          }
        });
      }, intersectionObserverConfig);

      const currentOverlayCanvas = overlayCanvas.current;
      if (currentOverlayCanvas) observer.observe(currentOverlayCanvas);
      return () => {
        if (currentOverlayCanvas) {
          observer.unobserve(currentOverlayCanvas);
        }
        abortController.abort();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blacklineViewMode]);

  //set the canvas width and height
  useEffect(() => {
    const canvas = overlayCanvas.current;
    const context = canvas.getContext('2d');
    context.canvas.width = sectionMetaData.width * scaleWithZoom;
    context.canvas.height = sectionMetaData.height * scaleWithZoom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionMetaData.height, sectionMetaData.width, scaleWithZoom]);

  const shouldDisplayBatchBox = () => {
    if (!isSideBySideView) return false;

    if (isLeftView) return true;

    return numberOfSourceElementSelected > numberOfTargetElementSelected;
  };

  const toggleAddNavigationModal = () => {
    setShowAddNavigationModal(!showAddNavigationModal);
  };

  const isSideBySideMode =
    isSideBySideView &&
    sideBySideViewMode?.viewMode === SIDE_BY_SIDE_COMPARE_MODE;
  const isAutoTieoutMode =
    isSideBySideView &&
    sideBySideViewMode?.viewMode === AUTO_TIEOUT_COMPARE_MODE;
  const isTrialBalanceMode =
    isSideBySideView && sideBySideViewMode?.viewMode === TRAIL_BALANCE_MODE;
  const isSideBySideRightView = isSideBySideMode && !isLeftView;
  const isSideBySideLeftView = isSideBySideMode && isLeftView;
  const isAutoTieoutRightView = isAutoTieoutMode && !isLeftView;

  const rightStatementCursorClassName =
    numberOfSourceElementSelected > numberOfTargetElementSelected &&
    `${BLOCK}--right-statement-selection-cursor`;

  return (
    <>
      {blacklineViewMode && (
        <BlacklineViewChanges
          ocrWords={ocrWords}
          isSectionLoaded={isSectionLoaded}
          elementList={elementList}
          isLeftView={isLeftView}
          scaleWithZoom={scaleWithZoom}
          sectionId={sectionMetaData.sectionId}
          blacklineCache={blacklineCache}
          dpi={dpi}
        />
      )}
      {isSectionLoaded && (
        <BookmarksDetailsContainerOCR
          bookmarksList={bookmarkList}
          sectionId={sectionMetaData.sectionId}
          scale={scaleWithZoom}
          leftSideView={isLeftView}
          dpi={dpi}
        />
      )}
      <NotesDetailsContainerOCR
        notesList={notesList}
        sectionId={sectionMetaData.sectionId}
        scale={scaleWithZoom}
        leftSideView={isLeftView}
        dpi={dpi}
      />
      {contentSearchResult.isLoaded && (
        <StatementContentSearchComponentOCR
          contentSearchResult={contentSearchResult}
          scale={scaleWithZoom}
          BLOCK={BLOCK}
          overlayCanvas={overlayCanvas}
          markerDisplayValue={false}
          calloutDisplayValue={false}
          showElementStatusAndFlag={showElementStatusAndFlag}
          dpi={dpi}
          pageSectionId={sectionMetaData.sectionId}
        />
      )}
      <ElementDetailsContainerOCR
        elementList={elementList}
        scale={scaleWithZoom}
        clearSelection={clearSelection}
        isPreviewPage={isPreviewPage}
        BLOCK={BLOCK}
        overlayCanvas={overlayCanvas}
        markerDisplayValue={markerDisplayValue}
        calloutDisplayValue={calloutDisplayValue}
        showElementStatusAndFlag={showElementStatusAndFlag}
        sectionId={sectionMetaData.sectionId}
        isLeftView={isLeftView}
        canvasID={canvasID}
      />
      {(isBatchModeSelected ||
        shouldDisplayBatchBox() ||
        shouldElementBatchSelection(selectMode)) &&
        box?.width > 0 &&
        box?.height > 0 && <HtmlOverlay box={box} />}
      {sectionMetaData?.sectionId && isSectionLoaded && (
        <PageHeaderOcr
          pageNumber={sectionMetaData?.sectionId}
          scaleWithZoom={scaleWithZoom}
        />
      )}
      <canvas
        id={canvasID}
        contentEditable={true}
        ref={overlayCanvas}
        className={classNames(`${BLOCK}__overlay`, {
          [`${BLOCK}--left-statement-cursor`]:
            isSideBySideLeftView ||
            ((!isSideBySideView ||
              ((isTrialBalanceMode || isAutoTieoutMode) && !isLeftView)) &&
              isModeForAddCursor(selectMode)),
          [`${BLOCK}--disabled`]: isTrialBalanceMode
            ? !isLeftView && isModeForAddCursor(selectMode)
              ? false
              : !isSnapshotViewLoaded
            : isAutoTieoutMode
              ? !isLeftView && isModeForAddCursor(selectMode)
                ? false
                : !leftSectionIdList.isLoaded
              : (isSideBySideView || isAutoTieoutRightView) &&
                !leftSectionIdList.isLoaded,
          [`${rightStatementCursorClassName}`]:
            isSideBySideRightView &&
            (isTrialBalanceMode ||
              isAutoTieoutRightView ||
              numberOfSourceElementSelected > numberOfTargetElementSelected),
          [`${BLOCK}--right-statement-cursor`]:
            !isTrialBalanceMode &&
            !isAutoTieoutRightView &&
            !(isAutoTieoutMode && !isLeftView && isModeForAddCursor(selectMode)) &&
            ((isSideBySideView && !leftSectionIdList.isLoaded) ||
              (isSideBySideRightView &&
                numberOfSourceElementSelected ===
                  numberOfTargetElementSelected)),
        })}
      ></canvas>
      {!isBatchModeSelected &&
        contextMenuData.selectedText &&
        !isDrawing &&
        !isPreviewPage &&
        !blacklineViewMode &&
        !showAddNavigationModal && (
          <StatementContextMenu
            contextMenuData={contextMenuData}
            clearSelection={clearSelection}
            BLOCK={BLOCK}
            toggleAddNavigationModal={toggleAddNavigationModal}
            overlayCanvas={overlayCanvas}
          />
        )}
      {showAddNavigationModal && (
        <AddNavigationOCRModal
          onClose={toggleAddNavigationModal}
          contextMenuData={contextMenuData}
          sectionId={sectionMetaData.sectionId}
          clearSelection={clearSelection}
        />
      )}
    </>
  );
};

export default React.memo(SectionContentOverlayOCR, areEqual);
